/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-alert */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Text } from '@sba/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import '@ui5/webcomponents-fiori/dist/illustrations/SearchEarth';
import moment from 'moment';
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { BusyIndicator, Button, MessageStrip } from '@ui5/webcomponents-react';
import useIsCommunityPlan from '../composer/hooks/useIsCommunityPlan';
import LoadingArea from '../composer/ui/LoadingArea';
import request from '../modules/request';
import trashIconImageUrl from './images/trash.svg';
import requestJson from '../modules/requestJson';

interface AppsListProps {
  search: string;
  canManageApps?: boolean;
}

interface App {
  id: string;
  title: string;
  updated_at: string | null;
  deleted_at: string | null;
}

async function fetchApps() {
  const data = await requestJson<{ apps: App[]; migrationCount: number }>('GET', '/app-editor/v1/projects');
  return {
    apps: data.apps,
    migrating: data.migrationCount > 0,
  };
}

async function deleteApp(appId: string) {
  return request('DELETE', `/app-editor/v1/projects/${encodeURIComponent(appId)}`);
}

async function copyApp(appId: string) {
  const response = await request('POST', `/app-editor/v1/projects/${appId}/copy`);
  return response.headers.get('Location');
}

// NOTE: This is an import from the old Rails based apps listing view, and it assumes the same CSS classes
export default function AppsList({ search, canManageApps }: AppsListProps) {
  const listQuery = useQuery({
    queryKey: ['apps'],
    queryFn: fetchApps,
  });
  const { apps, migrating } = listQuery.data || { apps: undefined, migrating: false };
  const queryClient = useQueryClient();
  const deleteAppMutation = useMutation({
    mutationFn: deleteApp,
    onSuccess: (_data, appId) => {
      // Filter out the app after a successful delete
      queryClient.setQueryData<{ apps: App[] }>(['apps'], (prevData) => ({
        apps: (prevData?.apps ?? []).filter((app) => app.id !== appId),
      }));
    },
    onError: () => {
      alert('Failed to delete the app!');
    },
  });
  const copyAppMutation = useMutation({
    mutationFn: copyApp,
    onSuccess: (url) => {
      // Redirect to the new app on successful copy
      if (url) {
        window.location.href = url;
      }
    },
    onError: () => {
      alert('Failed to copy the app!');
    },
  });
  const isCommunityPlan = useIsCommunityPlan();
  const placeholder = isCommunityPlan
    ? 'Click on "Create new" to start building your first app'
    : 'Head over to Lobby to see your projects';
  const isMutating = deleteAppMutation.isPending || copyAppMutation.isPending;
  const filteredApps =
    search && apps ? apps.filter((app) => app.title.toLowerCase().includes(search.toLowerCase())) : apps;
  if (!filteredApps) {
    return (
      <div className="app_index__cards">
        <LoadingArea />
      </div>
    );
  }
  if (!apps?.length && !migrating) {
    return (
      <div className="app_index__cards">
        <div className="app_index__empty">
          <p>It seems a bit empty here, doesn&#39;t it? {placeholder}</p>
        </div>
      </div>
    );
  }
  const listing = (
    <div className="app_index__cards">
      {filteredApps.map((app) => (
        <div className="app_index__card" key={app.id}>
          {canManageApps && (
            <a
              onClick={(event) => {
                event.preventDefault();
                copyAppMutation.mutate(app.id);
              }}
              className="app_index__card__copy"
            >
              <i className="fa fa-copy" title="Copy this app" />
            </a>
          )}

          <a
            className="app_index__card__export"
            href={`/app-editor/v1/projects/${app.id}/export`}
            aria-label="Export this app"
          >
            <i className="fa fa-download" title="Export this app" />
          </a>

          {canManageApps && (
            <a
              className="app_index__card__remove"
              onClick={(event) => {
                event.preventDefault();
                if (
                  !isMutating &&
                  window.confirm(`Are you sure you want to delete "${app.title}" and all of its content?`)
                ) {
                  deleteAppMutation.mutate(app.id);
                }
              }}
            >
              <img title="Remove this app" data-cy={app.title} alt="Remove this app" src={trashIconImageUrl} />
            </a>
          )}

          <a
            className="app_index__card__link"
            title={app.title}
            href={`/builder/applications/${encodeURIComponent(app.id)}`}
          >
            <h3>{app.title}</h3>

            <div className="app_index__card__updated">
              <p className="uppercase-tiny">updated</p>
              <p>{moment(app.updated_at).fromNow()}</p>
            </div>
          </a>
        </div>
      ))}
    </div>
  );

  if (migrating) {
    return (
      <>
        <br />
        <MessageStrip design="Critical" hideCloseButton>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <span>Migrating your apps</span>
            {listQuery.isRefetching ? (
              <BusyIndicator active delay={10} size="M" />
            ) : (
              <>
                <Text>
                  All of your apps are not yet available. This may take a few minutes. You can hit the reload button to
                  check updated state.
                </Text>
                <Button
                  style={{ height: '1.6rem', marginBlock: '-.25rem' }}
                  icon="refresh"
                  design="Transparent"
                  onClick={() => listQuery.refetch()}
                >
                  Reload
                </Button>
              </>
            )}
          </span>
        </MessageStrip>
        {listing}
      </>
    );
  }

  return listing;
}
